import { fetchJSON as fetchAPI } from './client';

export type SearchDashboard = {
  tenant_id: string;
  total_queries_tracked: number;
  avg_click_share_pct: number;
  avg_conversion_share_pct: number;
  high_opportunity_keywords: number;
  missing_keywords: number;
  growing_keywords: number;
  declining_keywords: number;
  lost_rankings: number;
  recovered_rankings: number;
  brand_visibility_pct: number;
  active_recommendations: number;
  datasets_available?: string[];
};

export type SearchQueryRecord = {
  id: string;
  query: string;
  search_volume: number;
  volume_trend_pct: number;
  click_share_pct: number;
  conversion_share_pct: number;
  impression_share_pct: number;
  organic_position?: number;
  sponsored_position?: number;
  sku?: string;
  intent?: string;
};

export type KeywordIntel = {
  id: string;
  keyword: string;
  category: string;
  search_volume: number;
  volume_trend_pct: number;
  click_share_pct: number;
  conversion_share_pct: number;
  organic_rank?: number;
  coverage_gap: boolean;
  skus?: string[];
};

export type RankingRecord = {
  id: string;
  sku: string;
  asin?: string;
  keyword: string;
  organic_rank: number;
  rank_trend?: string;
  rank_velocity: number;
  rank_stability_score: number;
  days_at_rank: number;
};

export type SearchTrend = {
  id: string;
  query_or_topic: string;
  category?: string;
  trend_pct: number;
  seasonality?: string;
  volume: number;
  intent?: string;
};

export type CategorySearchIntel = {
  id: string;
  category: string;
  search_demand: number;
  trend_pct: number;
  top_queries?: string[];
  customer_language?: string[];
  seasonality?: string;
};

export type PortfolioSearchCoverage = {
  id: string;
  brand_visibility_pct: number;
  keyword_coverage_pct: number;
  category_coverage_pct: number;
  keyword_overlap_score: number;
  cannibalization_count: number;
  missing_opportunities: number;
};

export type SearchRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  evidence?: string;
  query?: string;
  sku?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
};

const API_BASE = import.meta.env.VITE_SEARCHINTEL_API ?? '/api/v1/searchintel';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const searchintelApi = {
  dashboard: () => fetchJSON<SearchDashboard>('/dashboard'),
  listQueries: () => fetchJSON<SearchQueryRecord[]>('/queries'),
  listKeywords: () => fetchJSON<KeywordIntel[]>('/keywords'),
  listRankings: () => fetchJSON<RankingRecord[]>('/rankings'),
  listTrends: () => fetchJSON<SearchTrend[]>('/trends'),
  listCategory: () => fetchJSON<CategorySearchIntel[]>('/category'),
  portfolioCoverage: () => fetchJSON<PortfolioSearchCoverage>('/portfolio'),
  listRecommendations: () => fetchJSON<SearchRecommendation[]>('/recommendations'),
  generateRecommendations: () => fetchJSON<SearchRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  approveRecommendation: (id: string) =>
    fetchJSON<SearchRecommendation>(`/recommendations/${id}/approve`, { method: 'POST', body: JSON.stringify({}) }),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
